import type { BookingResponse } from '@/shared/api';

type Occupancy = Pick<BookingResponse, 'guestCount' | 'adults' | 'children' | 'infants'>;

/**
 * „2 Erwachsene, 4 Kinder" statt „6 Gäste".
 *
 * <p>Die Aufteilung ist nicht nur Anzeige: Kleinkinder zahlen keine Kurtaxe, ein Rate Plan
 * unterscheidet nach Alter, und die Rechnung rechnet damit. Wer nur die Summe sieht, kann eine
 * falsche Position nicht erkennen.</p>
 *
 * <p>Wo die Aufteilung fehlt oder nicht zur Summe passt (Altbestand, unvollständiger Import),
 * bleibt es bei der Gesamtzahl — eine erfundene Aufteilung wäre schlimmer als gar keine.</p>
 */
export function formatOccupancy(booking: Occupancy): string {
  const { guestCount, adults, children, infants } = booking;
  const total = adults + children + infants;
  if (total !== guestCount || total === 0) {
    return guestCount === 1 ? '1 Gast' : `${guestCount} Gäste`;
  }
  if (children === 0 && infants === 0) {
    return adults === 1 ? '1 Gast' : `${adults} Gäste`;
  }
  return [
    plural(adults, 'Erwachsener', 'Erwachsene'),
    plural(children, 'Kind', 'Kinder'),
    plural(infants, 'Kleinkind', 'Kleinkinder'),
  ]
    .filter((part): part is string => part !== null)
    .join(', ');
}

export function formatOccupancyBreakdown(booking: Occupancy): string {
  const { guestCount, adults, children, infants } = booking;
  if (adults + children + infants !== guestCount) return 'Aufteilung nicht verifiziert';
  return [
    plural(adults, 'Erwachsener', 'Erwachsene'),
    plural(children, 'Kind', 'Kinder'),
    plural(infants, 'Kleinkind', 'Kleinkinder'),
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

function plural(count: number, one: string, many: string): string | null {
  if (count <= 0) return null;
  return count === 1 ? `1 ${one}` : `${count} ${many}`;
}
